import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type ClinicianMaxUpgradeEntryPoint,
  type FTPConversionSource,
} from '@/models/analytics/features/payments';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import {
  atom,
  useAtom,
  type PrimitiveAtom,
  type SetStateAction,
  type WritableAtom,
} from 'jotai';
import { useCallback } from 'react';

// Reset on write rather than using module-level observe(), which retains
// subscriptions across server HMR rebuilds and leaks old bundles.
function modalOpenAtomWithReset<TReset>(
  companionAtom: PrimitiveAtom<TReset | null>
): WritableAtom<boolean, [SetStateAction<boolean>], void> {
  const openAtom = atom<boolean>(false);
  return atom(
    (get) => get(openAtom),
    (get, set, update: SetStateAction<boolean>) => {
      const isOpen = typeof update === 'function' ? update(get(openAtom)) : update;
      set(openAtom, isOpen);
      if (!isOpen) {
        set(companionAtom, null);
      }
    }
  );
}

export const upgradeProModalSourceAtom = atom<FTPConversionSource | null>(null);
export const upgradeProModalOpenAtom = modalOpenAtomWithReset(upgradeProModalSourceAtom);

export const clinicianUpgradeModalOpenAtom = atom<boolean>(false);
/** Set by the surface that opens the Clinician Max upgrade modal; cleared on close. */
export const clinicianMaxUpgradeModalEntryPointAtom =
  atom<ClinicianMaxUpgradeEntryPoint | null>(null);
export const clinicianMaxUpgradeModalOpenAtom = modalOpenAtomWithReset(
  clinicianMaxUpgradeModalEntryPointAtom
);
export const practiceUpgradeModalOpenAtom = atom<boolean>(false);
/**
 * Set to `true` when the Clinician/Practice upgrade modal is opened from the
 * post-90-day Clinician trial-ended recap modal ("Keep Clinician/Practice" CTA).
 * Drives the trial-recap variant of those modals (new title, no right panel)
 * even after the 90-day trial state has lapsed. Reset on modal close.
 */
export const upgradeModalFromTrialRecapAtom = atom<boolean>(false);
/** Contextual upgrade modal targeted at Clinician-trial users who have been power-using Evidence. */
export const evidencePowerUserUpgradeModalOpenAtom = atom<boolean>(false);
/** When set (e.g. on /dev/trial-modals), trial modals use this for days left and show "in trial" copy. */
export const trialModalPreviewDaysLeftAtom = atom<number | null>(null);
export const clinicianTrialModalOpenAtom = atom<boolean>(false);
const upgradeTogetherModalOpenAtom = atom<boolean>(false);
const isAskAiBlockedAtom = atom<boolean>(false);
const trialToPaidModalOpenAtom = atom<boolean>(false);

export const usePaywallStates = () => {
  const [trialToPaidModalOpen, setTrialToPaidModalOpen] = useAtom(
    trialToPaidModalOpenAtom
  );
  const [upgradeProModalOpen, setUpgradeProModalOpen] = useAtom(upgradeProModalOpenAtom);
  const [clinicianUpgradeModalOpen, setClinicianUpgradeModalOpen] = useAtom(
    clinicianUpgradeModalOpenAtom
  );
  const [clinicianMaxUpgradeModalOpen, setClinicianMaxUpgradeModalOpen] = useAtom(
    clinicianMaxUpgradeModalOpenAtom
  );
  const [practiceUpgradeModalOpen, setPracticeUpgradeModalOpen] = useAtom(
    practiceUpgradeModalOpenAtom
  );
  const [evidencePowerUserUpgradeModalOpen, setEvidencePowerUserUpgradeModalOpen] =
    useAtom(evidencePowerUserUpgradeModalOpenAtom);
  const [clinicianTrialModalOpen, setClinicianTrialModalOpen] = useAtom(
    clinicianTrialModalOpenAtom
  );
  const [upgradeTogetherModalOpen, setUpgradeTogetherModalOpen] = useAtom(
    upgradeTogetherModalOpenAtom
  );
  const [isAskAiBlocked, setIsAskAiBlocked] = useAtom(isAskAiBlockedAtom);

  const toggleUpgradeProModal = useCallback(
    (isOpen: boolean) => {
      setUpgradeProModalOpen(isOpen);
      if (isOpen) {
        track({
          options: { amplitude: false },
          event_name: TrackingEventName.ModalViewed,
          props: { screen_name: ScreenName.UpgradeToProModal },
        });
      }
    },
    [setUpgradeProModalOpen]
  );

  const toggleClinicianUpgradeModal = useCallback(
    (isOpen: boolean) => {
      setClinicianUpgradeModalOpen(isOpen);
      if (isOpen) {
        track({
          options: { amplitude: false },
          event_name: TrackingEventName.ModalViewed,
          props: { screen_name: ScreenName.UpgradeToClinicianModal },
        });
      }
    },
    [setClinicianUpgradeModalOpen]
  );

  /**
   * Legacy Evidence Pro paywall opener — Evidence Plus retired (GRO-3609).
   * Alias of toggleClinicianUpgradeModal so leftover callers keep compiling
   * until migrated; prefer toggleClinicianUpgradeModal at call sites.
   */
  const toggleUpgradeEvidenceProModal = toggleClinicianUpgradeModal;

  const toggleClinicianMaxUpgradeModal = useCallback(
    (isOpen: boolean) => {
      setClinicianMaxUpgradeModalOpen(isOpen);
    },
    [setClinicianMaxUpgradeModalOpen]
  );

  const togglePracticeUpgradeModal = useCallback(
    (isOpen: boolean) => {
      setPracticeUpgradeModalOpen(isOpen);
      if (isOpen) {
        track({
          options: { amplitude: false },
          event_name: TrackingEventName.ModalViewed,
          props: { screen_name: ScreenName.UpgradeToPracticeModal },
        });
      }
    },
    [setPracticeUpgradeModalOpen]
  );

  const toggleClinicianTrialModal = useCallback(
    (isOpen: boolean) => {
      setClinicianTrialModalOpen(isOpen);
    },
    [setClinicianTrialModalOpen]
  );

  const toggleUpgradeTogetherModal = useCallback(
    (isOpen: boolean) => {
      setUpgradeTogetherModalOpen(isOpen);
    },
    [setUpgradeTogetherModalOpen]
  );

  const toggleTrialToPaidModal = useCallback(
    (isOpen: boolean) => {
      setTrialToPaidModalOpen(isOpen);
    },
    [setTrialToPaidModalOpen]
  );

  const toggleIsAskAiBlocked = useCallback(
    (isOpen: boolean) => {
      setIsAskAiBlocked(isOpen);
    },
    [setIsAskAiBlocked]
  );

  const toggleEvidencePowerUserUpgradeModal = useCallback(
    (isOpen: boolean) => {
      setEvidencePowerUserUpgradeModalOpen(isOpen);
      if (isOpen) {
        track({
          options: { amplitude: false },
          event_name: TrackingEventName.ModalViewed,
          props: { screen_name: ScreenName.UpgradeToEvidencePowerUserModal },
        });
      }
    },
    [setEvidencePowerUserUpgradeModalOpen]
  );

  return {
    trialToPaidModalOpen,
    upgradeProModalOpen,
    /** Always false — Evidence Pro modal deleted (GRO-3609) */
    upgradeEvidenceProModalOpen: false as const,
    clinicianUpgradeModalOpen,
    clinicianMaxUpgradeModalOpen,
    practiceUpgradeModalOpen,
    evidencePowerUserUpgradeModalOpen,
    clinicianTrialModalOpen,
    upgradeTogetherModalOpen,
    isAskAiBlocked,
    toggleTrialToPaidModal,
    toggleUpgradeProModal,
    toggleUpgradeEvidenceProModal,
    toggleClinicianUpgradeModal,
    toggleClinicianMaxUpgradeModal,
    togglePracticeUpgradeModal,
    toggleClinicianTrialModal,
    toggleUpgradeTogetherModal,
    toggleIsAskAiBlocked,
    toggleEvidencePowerUserUpgradeModal,
  };
};
